import { env } from '@/lib/env'

/**
 * THE VOICE-ANSWER CLIENT (Build 10b, Kelvo).
 *
 * Talks to the owner-only voice-note routes (workers/api.ts) and holds every
 * word the Questions and Facts pages say about a recording, so the wording is
 * tested in one place (tests/voice-notes-client.test.ts) and not in JSX.
 * React-free, like src/lib/renewals.ts.
 *
 * NEVER TRUST THE WIRE. A draft with no id or no words is dropped rather than
 * drawn: a Play button over nothing, or an empty "draft" that fills the
 * answer box with nothing, would both be lies.
 */

/** A voice draft waiting on a question (the engine's VoiceDraft). */
export interface VoiceNoteDraft {
  id: string
  transcript: string
  /** True when the machine was unsure enough that he should read every word. */
  lowConfidence: boolean
  seconds: number
}

/** The recording behind an approved answer, on the Facts page. */
export interface FactRecording {
  id: string
  seconds: number
}

function whole(n: unknown): number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
}

/** One `voiceDraft` off the wire, or null. A malformed `lowConfidence` reads as
 *  TRUE: telling him to read carefully when he need not costs a glance; the
 *  other way costs a misheard word in the corpus. */
export function toVoiceNoteDraft(raw: unknown): VoiceNoteDraft | null {
  if (raw === null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (typeof r.id !== 'string' || !r.id) return null
  if (typeof r.transcript !== 'string' || !r.transcript.trim()) return null
  return {
    id: r.id,
    transcript: r.transcript,
    lowConfidence: r.lowConfidence !== false,
    seconds: whole(r.seconds),
  }
}

/** One fact's `voiceNote` off the wire, or null (nothing to play). */
export function toFactRecording(raw: unknown): FactRecording | null {
  if (raw === null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (typeof r.id !== 'string' || !r.id || r.hasRecording !== true) return null
  return { id: r.id, seconds: whole(r.seconds) }
}

// --- Words -------------------------------------------------------------------

/** "0:23", "1:05". */
export function formatLength(seconds: number): string {
  const s = whole(seconds)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** The line over a draft. Says what it is, and that nothing is saved yet. */
export function draftHeading(d: VoiceNoteDraft): string {
  return `From your voice note (${formatLength(d.seconds)}). Check every word, then press Answer. Nothing is saved until you do.`
}

/** Shown under the heading when the machine was unsure. */
export const LOW_CONFIDENCE_LINE = 'Some of it was hard to make out. Play it back as you read.'

/** The line on a fact that came from a voice note. */
export function factRecordingLine(r: FactRecording): string {
  return `Answered by voice (${formatLength(r.seconds)}). Only you can play the recording.`
}

/** Asked before a recording behind an answer is deleted. */
export const DELETE_RECORDING_CONFIRM = 'Delete the recording? The answer stays, word for word.'

/**
 * Where a new transcript goes. An empty box takes it; a box he has already
 * typed in keeps what he typed and gets the transcript after a blank line, so
 * talking never throws away typing.
 */
export function withTranscript(current: string, transcript: string): string {
  return current.trim() ? `${current.trimEnd()}\n\n${transcript}` : transcript
}

/**
 * What the box keeps when a draft is thrown away: emptied if it still holds
 * exactly what the machine heard, otherwise left alone — those are his words
 * now, and throwing away a recording must not throw away his edits.
 */
export function afterDiscard(current: string, transcript: string): string {
  return current.trim() === transcript.trim() ? '' : current
}

// --- Requests ------------------------------------------------------------------

export class VoiceNoteError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    /** The engine's error code, e.g. 'draft_exists', 'rate_limited'. */
    readonly code?: string,
    /** True when recording again may well work. */
    readonly canRetry = false,
  ) {
    super(message)
    this.name = 'VoiceNoteError'
  }
}

const SIGNED_OUT = 'Your sign-in has expired. Please sign in again.'
const OFFLINE = 'No connection. Check your signal and try again.'

async function call(
  token: string | null,
  path: string,
  init: { method: 'POST' | 'DELETE' | 'GET'; body?: Blob },
): Promise<Response> {
  if (!token) throw new VoiceNoteError(SIGNED_OUT, 401)
  try {
    return await fetch(`${env.VITE_API_URL}${path}`, {
      method: init.method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body ? { 'Content-Type': 'audio/wav' } : {}),
      },
      body: init.body,
    })
  } catch {
    throw new VoiceNoteError(OFFLINE, undefined, undefined, true)
  }
}

async function failure(res: Response, fallback: string): Promise<VoiceNoteError> {
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null
  const message = typeof body?.message === 'string' && body.message ? body.message : fallback
  const code = typeof body?.error === 'string' ? body.error : undefined
  if (res.status === 401) return new VoiceNoteError(SIGNED_OUT, 401, code)
  return new VoiceNoteError(message, res.status, code, body?.canRetry === true)
}

/** Send a recording for one waiting question; the draft comes back. */
export async function recordVoiceNote(token: string | null, questionId: string, wav: Blob): Promise<VoiceNoteDraft> {
  const res = await call(token, `/api/admin/questions/${encodeURIComponent(questionId)}/voice-note`, {
    method: 'POST',
    body: wav,
  })
  if (!res.ok) throw await failure(res, 'That recording did not save. Try again, or type your answer.')
  const body = (await res.json().catch(() => null)) as { draft?: unknown } | null
  const draft = toVoiceNoteDraft(body?.draft)
  if (!draft) throw new VoiceNoteError('That recording did not save. Try again, or type your answer.', res.status)
  return draft
}

/** Throw a draft away. A draft that is already gone is not an error. */
export async function discardVoiceNote(token: string | null, id: string): Promise<void> {
  const res = await call(token, `/api/admin/voice-notes/${encodeURIComponent(id)}`, { method: 'DELETE' })
  if (res.ok || res.status === 404) return
  throw await failure(res, 'Could not discard the draft. Try again.')
}

/** The recording's bytes, to play. */
export async function fetchVoiceNoteAudio(token: string | null, id: string): Promise<Blob> {
  const res = await call(token, `/api/admin/voice-notes/${encodeURIComponent(id)}/audio`, { method: 'GET' })
  if (res.status === 404) throw new VoiceNoteError('That recording is no longer available.', 404)
  if (!res.ok) throw await failure(res, 'Could not play the recording. Try again.')
  return res.blob()
}

/** Delete the recording behind an approved answer; the answer stays. */
export async function deleteVoiceRecording(token: string | null, id: string): Promise<void> {
  const res = await call(token, `/api/admin/voice-notes/${encodeURIComponent(id)}/audio`, { method: 'DELETE' })
  if (res.ok || res.status === 404) return
  throw await failure(res, 'Could not delete the recording. Try again.')
}
